import * as i0 from '@angular/core';
import { Injectable, Inject, Optional, ViewEncapsulation, NgModule } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i2 from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as i1 from 'rxjs';
import { Observable, of, takeWhile, Subject } from 'rxjs';
import * as i1$1 from '@angular/platform-browser';
import { Meta } from '@angular/platform-browser';
import merge from 'lodash-es/merge';
import * as i4 from '@wdpr/ra-angular-page-key';
import { PageKeyModule } from '@wdpr/ra-angular-page-key';
import * as i5 from '@wdpr/ra-angular-logger';
import { LoggerModule } from '@wdpr/ra-angular-logger';
import isUndefined from 'lodash-es/isUndefined';

class I18nService {
    constructor(document, options, languageChangeSubject) {
        this.document = document;
        this.languageChangeSubject = languageChangeSubject;
        this.languageList = [
            {
                code: 'en-us',
                text: 'EN'
            }
        ];
        this.serverPath = 'assets/seo/i18n';
        this.seoOptions = merge(this.seoOptions, options);
        this.useTranslate = this.seoOptions.useTranslate;
        this.serverPath = this.seoOptions.serverPath;
        this.languageList = merge(this.languageList, this.seoOptions.languageList);
        this.setLangCode(this.languageList[0].code);
        this.init();
    }
    init() {
        this.languageChange = new Observable((observer) => {
            this.languageChangeSubject.subscribe(observer);
        });
    }
    setLangCode(lang) {
        if (lang !== this.languageCode) {
            this.document.documentElement.lang = lang;
            this.languageCode = lang;
            this.languageChangeSubject.next(this.languageCode);
        }
    }
    getLangCode() {
        return this.languageCode;
    }
    setLangList(langList) {
        this.languageList = langList;
    }
    getLangList() {
        return this.languageList;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: I18nService, deps: [{ token: DOCUMENT }, { token: 'SEO_OPTIONS', optional: true }, { token: i1.Subject }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: I18nService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: I18nService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['SEO_OPTIONS']
                }] }, { type: i1.Subject }] });

class SeoDataService {
    constructor(i18nService, options, http) {
        this.i18nService = i18nService;
        this.options = options;
        this.http = http;
        this.serverUrl = '';
        this.serverPath = 'assets/seo';
        this.seoOptions = {
            serverUrl: '',
            useTranslate: true,
            serverPath: 'assets/seo',
            manualBootstrap: false
        };
        this.seoOptions = merge(this.seoOptions, options);
        this.serverUrl = this.seoOptions.serverUrl;
        this.serverPath = this.seoOptions.serverPath;
    }
    loadData(pageKey) {
        if (this.options.customSeoRetrieval) {
            return of(false);
        }
        else {
            let relativeUrl;
            if (this.i18nService.useTranslate) {
                relativeUrl = `${this.i18nService.serverPath}/${pageKey}/${this.i18nService.languageCode}.json`;
            }
            else {
                relativeUrl = `${this.serverPath}/${pageKey}.json`;
            }
            const url = this.serverUrl ?
                this.serverUrl + relativeUrl :
                relativeUrl;
            return this.http.get(url);
        }
    }
    setServerUrl(url) {
        this.serverUrl = url;
    }
    getServerUrl() {
        return this.serverUrl;
    }
    getManualBootstrapState() {
        return this.seoOptions.manualBootstrap;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoDataService, deps: [{ token: I18nService }, { token: 'SEO_OPTIONS', optional: true }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoDataService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoDataService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: I18nService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['SEO_OPTIONS']
                }] }, { type: i2.HttpClient }] });

// get a reference to the window object
function _window() {
    if (globalThis.window) {
        return window;
    }
}
class WindowRef {
    getNativeWindow() {
        return _window();
    }
    /**
     * Check to see if the error handling should be enabled
     */
    isErrorHandlerEnabled() {
        const windowObj = _window();
        // If define exists on the window object we're in test mode.
        return (windowObj && isUndefined(windowObj.define));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: WindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: WindowRef }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: WindowRef, decorators: [{
            type: Injectable
        }] });

class SeoService {
    constructor(meta, title, i18nService, seoDataService, document, rendererFactory, pageKeyService, logger, window, options) {
        this.meta = meta;
        this.title = title;
        this.i18nService = i18nService;
        this.seoDataService = seoDataService;
        this.document = document;
        this.rendererFactory = rendererFactory;
        this.pageKeyService = pageKeyService;
        this.logger = logger;
        this.window = window;
        this.subscription = true;
        this.createdLinkElements = [];
        this.seoOptions = merge({
            includeMetaTag: {
                alternate: true,
                keywords: true,
                description: true,
                canonical: true
            }
        }, options);
        if (!this.seoDataService.getManualBootstrapState()) {
            this.init();
        }
    }
    init() {
        this.languageChangeListener();
        this.pageKeyListener();
    }
    setServerUrl(url) {
        this.seoDataService.setServerUrl(url);
    }
    pageKeyListener() {
        this.pageKeyService
            .pipe(takeWhile(() => this.subscription))
            .subscribe({
            next: (pageKey) => {
                this.setSeo(pageKey.key);
            },
            error: (error) => this.onErrorMessage(error),
        });
    }
    onErrorMessage(error) {
        this.logger.error(error);
    }
    setSeo(pageKey) {
        this.pageKey = pageKey;
        this.loadSeoData(pageKey);
    }
    setSeoData(data) {
        this.seoData = data;
        this.setTags();
    }
    loadSeoData(pageKey) {
        this.seoDataService.loadData(pageKey).subscribe((data) => {
            if (data) {
                this.setSeoData(data);
            }
        }, (err) => console.log('Data fetched from external service'));
    }
    languageChangeListener() {
        this.i18nService.languageChange.subscribe((languageCode) => {
            this.loadSeoData(this.pageKey);
        });
    }
    setTags() {
        const langList = this.i18nService.getLangList();
        const pageTitle = this.seoData.title || '';
        const description = this.seoData.description || '';
        const keywords = this.seoData.keywords || '';
        const canonicalUrlOverride = this.seoData.canonicalUrl || this.window.getNativeWindow()?.location?.href;
        const customTagList = this.seoData.customTags || [];
        this.removeLinkElements();
        this.title.setTitle(pageTitle);
        if (this.seoOptions.includeMetaTag.description) {
            this.meta.updateTag({
                name: 'description',
                content: description
            });
        }
        if (this.seoOptions.includeMetaTag.keywords) {
            this.meta.updateTag({
                name: 'keywords',
                content: keywords
            });
        }
        if (this.seoOptions.includeMetaTag.canonical) {
            this.createLinkElement({
                rel: 'canonical',
                href: canonicalUrlOverride
            });
        }
        if (this.seoOptions.includeMetaTag.alternate) {
            langList.map((lang) => {
                const href = `${this.window.getNativeWindow().location.origin}/${lang.code}${this.window.getNativeWindow().location.pathname}`;
                this.createLinkElement({
                    rel: 'alternate',
                    href: href
                });
                return;
            });
        }
        for (let tagIndex = 0; tagIndex < customTagList.length; tagIndex++) {
            const customTag = customTagList[tagIndex];
            this.meta.updateTag({
                property: customTag['property'],
                content: customTag['content']
            });
        }
    }
    removeLinkElements() {
        this.createdLinkElements.map((linkElement) => {
            linkElement.remove();
        });
        this.createdLinkElements = [];
    }
    createLinkElement(tag) {
        try {
            const renderer = this.rendererFactory.createRenderer(this.document, {
                id: '-1',
                encapsulation: ViewEncapsulation.None,
                styles: [],
                data: {}
            });
            const linkElement = renderer.createElement('link');
            const headElement = this.document.head;
            if (headElement === null) {
                throw new Error('header element not found');
            }
            Object.keys(tag).forEach((prop) => {
                return renderer.setAttribute(linkElement, prop, tag[prop]);
            });
            this.createdLinkElements.push(linkElement);
            renderer.appendChild(headElement, linkElement);
        }
        catch (error) {
            console.error(error);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoService, deps: [{ token: i1$1.Meta }, { token: i1$1.Title }, { token: I18nService }, { token: SeoDataService }, { token: DOCUMENT }, { token: i0.RendererFactory2 }, { token: i4.PageKeyService }, { token: i5.LoggerService }, { token: WindowRef }, { token: 'SEO_OPTIONS', optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.Meta }, { type: i1$1.Title }, { type: I18nService }, { type: SeoDataService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.RendererFactory2 }, { type: i4.PageKeyService }, { type: i5.LoggerService }, { type: WindowRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['SEO_OPTIONS']
                }] }] });

class SeoModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: SeoModule, imports: [CommonModule,
            PageKeyModule,
            LoggerModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoModule, providers: [
            {
                provide: 'SEO_OPTIONS',
                useValue: {
                    useTranslate: false,
                    serverPath: 'assets/seo/i18n',
                    languageList: [{
                            code: 'en-us',
                            text: 'EN'
                        }],
                    includeMetaTag: {
                        alternate: true,
                        canonical: true,
                        keywords: true,
                        description: true
                    }
                }
            },
            SeoService,
            I18nService,
            Subject,
            SeoDataService,
            Meta,
            WindowRef,
            provideHttpClient(withInterceptorsFromDi()),
        ], imports: [CommonModule,
            PageKeyModule,
            LoggerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: SeoModule, decorators: [{
            type: NgModule,
            args: [{ exports: [],
                    declarations: [], imports: [CommonModule,
                        PageKeyModule,
                        LoggerModule], providers: [
                        {
                            provide: 'SEO_OPTIONS',
                            useValue: {
                                useTranslate: false,
                                serverPath: 'assets/seo/i18n',
                                languageList: [{
                                        code: 'en-us',
                                        text: 'EN'
                                    }],
                                includeMetaTag: {
                                    alternate: true,
                                    canonical: true,
                                    keywords: true,
                                    description: true
                                }
                            }
                        },
                        SeoService,
                        I18nService,
                        Subject,
                        SeoDataService,
                        Meta,
                        WindowRef,
                        provideHttpClient(withInterceptorsFromDi()),
                    ] }]
        }] });

/*
 * Public API Surface of ra-angular-seo-metadata
 */

/**
 * Generated bundle index. Do not edit.
 */

export { I18nService, SeoDataService, SeoModule, SeoService, WindowRef };
